import { BlobReader, ZipReader } from '@zip.js/zip.js';
import { XMLParser } from 'fast-xml-parser';
import { ImportLayersElements } from '@/typings/layers/layers';
import { getFileText, getPageDimensions, parseLayerNode } from '@/utils/CanvaFileParserUtils';
import { IFileParserStrategy } from '@/typings/parsers/externalFileParser';
import { FileParserType } from '@/typings/parsers/fileParserTypes';
import { SlideJson } from '@/typings/parsers/pptxParserTypes';
import * as OrderedSlide from '@/typings/parsers/pptxOrderedLayersTypes';
export class CanvaFileParser implements IFileParserStrategy<ZipReader<Blob>> {
    type: FileParserType = 'CANVA';
    page = 1;

    async parse(buffer: ArrayBuffer): Promise<ZipReader<Blob>> {
        const blob = new Blob([buffer]);
        return new ZipReader(new BlobReader(blob));
    }

    async extractLayers(zipReader: ZipReader<Blob>, importLayers: ImportLayersElements): Promise<ImportLayersElements> {
        const xmlParserProperties = {
            ignoreAttributes: false,
            attributeNamePrefix: '@_',
            removeNSPrefix: true
        };
        const parser = new XMLParser(xmlParserProperties);

        const parserOrdered = new XMLParser({
            ...xmlParserProperties,
            preserveOrder: true
        });

        if (importLayers.selectedPage) {
            this.page = importLayers.selectedPage;
        }
        const slidePath = `ppt/slides/slide1.xml`;
        const slideXml = await getFileText(zipReader, slidePath);
        const slideJson: SlideJson = parser.parse(slideXml);
        const orderedSlideJson: OrderedSlide.RootTree[] = parserOrdered.parse(slideXml);
        // 2. Get the Xml file relationships
        const relsPath = `ppt/slides/_rels/slide1.xml.rels`;
        let relationships: Record<string, string> = {};
        try {
            const relsXml = await getFileText(zipReader, relsPath);
            const relsJson = parser.parse(relsXml) as {
                Relationships: { Relationship: any };
            };
            let relArray = relsJson.Relationships.Relationship;
            if (!Array.isArray(relArray)) relArray = [relArray];
            relArray.forEach((rel: any) => {
                relationships[rel['@_Id']] = rel['@_Target'];
            });
        } catch (error) {
            console.warn('No relationships found:', error);
        }

        // 3. Get the main node
        const pageNode = slideJson.sld?.cSld?.spTree;
        if (!pageNode) throw new Error('No shape tree found in slide');

        // Recursively push images and texts into importLayers.elements.
        const page = importLayers.pages.find((page) => page.pageNumber == this.page);
        if (!page) return importLayers;
        const pageElements = await parseLayerNode(pageNode, relationships, zipReader, orderedSlideJson);
        page.elements = pageElements;
        if (!importLayers.includeDimensions) return importLayers;

        const presentationXml = await getFileText(zipReader, 'ppt/presentation.xml');
        const presentationJson = parser.parse(presentationXml);
        const getPageDimensionsObject = presentationJson.presentation['sldSz'];
        const dimension = getPageDimensions(getPageDimensionsObject);

        const background = slideJson.sld.cSld.bg;
        if (background?.bgPr?.solidFill?.srgbClr) {
            page.backgroundColor = `#${background.bgPr.solidFill.srgbClr['@_val']}`;
        }

        page.artWorkWidth = dimension.width;
        page.artWorkHeight = dimension.height;
        return importLayers;
    }
}
